// Gemeinsame Metadaten des Onboarding-Wizards. 1:1-Port von src/components/wizard/config.js.
import type { StepId, StammdatenSubId } from "@/features/onboarding/progress";

export interface StepMeta {
  id: StepId;
  title: string;
  desc: string;
}

export const STEPS_META: StepMeta[] = [
  { id: "willkommen", title: "Willkommen", desc: "Plattform-Einführung" },
  { id: "stammdaten", title: "Stammdaten", desc: "Unternehmensstruktur" },
  { id: "auftraege", title: "Aufträge & Checklisten", desc: "Auftragseinrichtung" },
  { id: "kontokonfiguration", title: "Konto-Konfiguration", desc: "Individuelle Kontoanpassungen" },
  { id: "berichte", title: "Reports", desc: "Reports einrichten" },
  { id: "abschluss", title: "Abschluss", desc: "Fertigstellung und Terminbuchung" }
];

export interface StammdatenSubMeta {
  id: StammdatenSubId;
  title: string;
  desc: string;
  icon: string;
}

export const STAMMDATEN_SUB: StammdatenSubMeta[] = [
  { id: "firmendaten", title: "Firmendaten", desc: "Zentrale Kontaktdaten", icon: "building-2" },
  { id: "benutzerteams", title: "Teams & Benutzer", desc: "Mitarbeiter & Rechte", icon: "users" },
  { id: "fahrzeuge", title: "Fahrzeuge", desc: "Fuhrpark & Kennzeichen", icon: "truck" },
  { id: "auftragsarten", title: "Auftragsarten", desc: "Workflow-Typen & Farben", icon: "palette" },
  { id: "adresstypen", title: "Adresstypen & Adressen", desc: "Standorte & CSV-Import", icon: "map-pin" },
  { id: "ressourcen", title: "Ressourcen", desc: "Arbeitsmittel & CSV-Import", icon: "package" }
];

export interface RoleMatrixFunction {
  name: string;
  roles: string[];
}

export const ROLE_MATRIX_HEADERS: string[] = [
  "Administrator",
  "Operator & Administrator",
  "Operator",
  "Disponent",
  "Mobiler Erfasser",
  "Bearbeiter/Disponent",
  "Selbstverwalter",
  "Beobachter",
  "Basic",
  "Kundennutzer"
];

export const ROLE_MATRIX_FUNCTIONS: RoleMatrixFunction[] = [
  { name: "Einstellungs- & Konfigurationsrechte", roles: ["✅", "✅", "❌", "❌", "❌", "❌", "❌", "❌", "❌", "❌"] },
  { name: "Aufträge sehen", roles: ["✅", "✅", "✅", "✅", "☑", "☑ / ✅", "☑", "✅", "❌", "☑"] },
  { name: "Aufträge erstellen", roles: ["❌", "✅", "✅", "✅", "❌", "❌ / ✅", "☑", "❌", "❌", "☑"] },
  { name: "Aufträge ändern", roles: ["❌", "✅", "✅", "✅", "❌", "❌ / ✅", "☑", "❌", "❌", "☑"] },
  { name: "Aufträge ausführen & fertigstellen", roles: ["❌", "✅", "✅", "❌", "☑", "☑ / ✅", "☑", "❌", "❌", "❌"] },
  { name: "Aufträge abschließen", roles: ["❌", "✅", "✅", "✅", "❌", "❌ / ✅", "☑", "❌", "❌", "❌"] },
  { name: "Dispo-Board", roles: ["⚠", "✅", "✅", "✅", "❌", "❌ / ✅", "❌", "⚠", "❌", "❌"] },
  { name: "Karten Ansicht", roles: ["⚠", "✅", "✅", "✅", "❌", "❌ / ✅", "✅", "⚠", "❌", "❌"] },
  { name: "Abrechnung/ Exportieren", roles: ["⚠", "✅", "✅", "✅", "❌", "❌ / ✅", "❌", "❌", "❌", "❌"] },
  { name: "Report", roles: ["⚠", "✅", "✅", "✅", "❌", "❌ / ✅", "❌", "❌", "❌", "❌"] },
  { name: "Adressbuch: Adressen anlegen & nutzen", roles: ["⚠", "✅", "✅", "✅", "✅", "❌ / ✅", "❌", "❌", "❌", "❌"] },
  { name: "Arbeitszeiten & Abwesenheiten", roles: ["⚠", "✅", "✅", "✅", "✅", "✅", "✅", "✅", "✅", "❌"] }
];

export const TEAM_ROLES_EXPLANATION: Array<{ name: string; desc: string; rights: string }> = [
  { name: "Kein Mitglied", desc: "Benutzer gehört dem Team nicht an.", rights: "keine Einsicht" },
  { name: "Zuschauer", desc: "Benutzer gehört dem Team nicht an, soll allerdings Team einsehen können.", rights: "Einsicht im Team" },
  { name: "Manager", desc: "Benutzer ist Manager des Teams, ist allerdings kein Mitglied des Teams.", rights: "Einsicht im Team & Berechtigungen" },
  { name: "Mitglied", desc: "Benutzer ist Mitglied des Teams.", rights: "Nutzung" },
  { name: "Mitglied + Zuschauer", desc: "Benutzer ist Mitglied des Teams und soll Team einsehen können.", rights: "Einsicht im Team & Nutzung" },
  { name: "Mitglied + Manager", desc: "Benutzer ist Manager des Teams und gleichzeitig Mitglied.", rights: "Nutzung, Einsicht im Team & Berechtigungen" }
];

export const ROLE_DESCRIPTIONS: Array<{ name: string; example: string; license: string; desc: string }> = [
  {
    name: "Administrator",
    example: "IT-Abteilung, Systemverantwortlicher",
    license: "Keine Lizenz benötigt",
    desc: "Der Benutzer übernimmt ausschließlich administratorische Aufgaben zu Benutzern, Ressourcen etc. und kann darüber hinaus als Zuschauer einsehen. Die Rolle dient i.d.R. als allgemeiner Config-Account."
  },
  {
    name: "Operator & Administrator",
    example: "Niederlassungsleiter, Dispositionsleitung",
    license: "Office-Lizenz",
    desc: "Der Benutzer hat sämtliche Berechtigungen zum Disponieren, Bearbeiten & Abschließen. Darüber hinaus besitzt er die Admin-Rechte."
  },
  {
    name: "Operator",
    example: "Hauptdisponent, Büro-Mitarbeiter",
    license: "Office-Lizenz",
    desc: "Der Benutzer besitzt dieselben vollständigen Berechtigungen wie die Rolle Operator & Admin, nur ohne Adminrechte."
  },
  {
    name: "Disponent",
    example: "Zusatz-Disponent, Sachbearbeiter",
    license: "Office-Lizenz",
    desc: "Der Benutzer hat dieselben Berechtigungen wie der Operator, nur kann dieser Aufträge nicht befüllen und die Bearbeitung fertigstellen. Aufträge können erst abgeschlossen werden, nachdem sie vom Befüller fertiggestellt wurden."
  },
  {
    name: "Mobiler Erfasser",
    example: "Fahrer, Monteur, Techniker vor Ort",
    license: "Mobile-Lizenz",
    desc: "Der Benutzer führt Aufträge aus und stellt sie anschließend auf Fertig gestellt. Er nutzt cleverfleet hauptsächlich zum Befüllen der Aufträge."
  },
  {
    name: "Bearbeiter/Disponent",
    example: "Springer, mitarbeitender Meister",
    license: "Mobile-Lizenz",
    desc: "Dies ist eine Wechselrolle. Es kann zwischen dem Mobilen Erfasser und dem Disponenten per Switch-Funktion gewechselt werden. Je nach Status stehen dem Nutzer die entsprechenden Funktionsmöglichkeiten zur Verfügung."
  },
  {
    name: "Selbstverwalter",
    example: "Subunternehmer, selbstständige Fahrer",
    license: "Mobile-Lizenz",
    desc: "Der Benutzer ist nur für seine eigenen Aufträge zuständig. Er erstellt, sieht und bearbeitet nur für sich selbst."
  },
  {
    name: "Beobachter",
    example: "Geschäftsführung (nur Statistik), Controller",
    license: "Office-Lizenz",
    desc: "Der Benutzer kann die Dispo einsehen, allerdings selbst nicht mitarbeiten."
  },
  {
    name: "Basic",
    example: "Lagerist, Reinigungskraft",
    license: "Zeiterfassungs-Lizenz",
    desc: "Der Benutzer arbeitet nicht mit der Disposition. Er nutzt cleverfleet für Zeiterfassung, Arbeitszeiten & Abwesenheiten."
  },
  {
    name: "Kundennutzer",
    example: "Externer Auftraggeber, Großkunde",
    license: "Kunden-Lizenz",
    desc: "Mit dieser Rolle können Kunden Aufträge für das Unternehmen erstellen und verfolgen. Es handelt sich um einen Kundenaccount."
  }
];

export interface ConfigCategory {
  title: string;
  questions: Array<{ id: string; label: string }>;
}

export const CONFIG_CATEGORIES: ConfigCategory[] = [
  {
    title: "1. Datenimport & -export",
    questions: [
      { id: "erp_import", label: "ERP-Auftragsimport notwendig (z. B. SAP, Navision)?" },
      { id: "erp_export", label: "ERP-Rückexport nach Tourabschluss?" },
      { id: "pdf_export", label: "Automatisches Generieren von Kunden-Lieferscheinen als PDF?" },
      { id: "csv_client_sync", label: "Wöchentlicher Kundendaten-Abgleich via CSV-Schnittstelle?" },
      { id: "automatic_report_mail", label: "Automatischen Berichtsversand per E-Mail konfigurieren?" },
      { id: "external_billing_trigger", label: "Rechnungsfreigabe an externes Fakturasystem übergeben?" }
    ]
  },
  {
    title: "2. GPS & Telematik",
    questions: [
      { id: "gps_tracking", label: "Live-Fahrzeugortung (Spurverfolgung) im Dispo-Board?" },
      { id: "geofencing_alert", label: "Geofencing-Benachrichtigung bei Erreichen des Kundenstandorts?" },
      { id: "speed_limit_warning", label: "Warnung bei Geschwindigkeitsüberschreitungen an Admin senden?" },
      { id: "driver_id_key", label: "Fahreridentifikation über RFID-Key im Fahrzeug aktivieren?" },
      { id: "obd_telemetry", label: "OBD-Motordaten auslesen (z. B. Kraftstoffverbrauch, Fehlercodes)?" },
      { id: "geotab_active", label: "Direkte Geotab-API Integration freischalten?" }
    ]
  },
  {
    title: "3. Abrechnung & Faktura",
    questions: [
      { id: "easybill_direct", label: "Automatische Schnittstelle zu Easybill aktivieren?" },
      { id: "vat_split", label: "Mehrwertsteuer-Splittung für grenzüberschreitende Touren?" },
      { id: "sepa_export", label: "SEPA-Lastschrift-Export für Kundenabrechnung?" },
      { id: "automatic_invoice_pdf", label: "Direkte PDF-Rechnungserstellung nach Unterschrift?" },
      { id: "payment_status_sync", label: "Zahlungsstatus-Abgleich mit Bankkonten?" },
      { id: "reminder_mails", label: "Automatischen Rechnungsmahnlauf bei Verzug?" }
    ]
  },
  {
    title: "4. Sicherheits- & Benutzerrechte",
    questions: [
      { id: "two_factor_auth", label: "Zwei-Faktor-Authentifizierung (2FA) für Admins erzwingen?" },
      { id: "pass_expiry_90", label: "Passwort-Ablauf nach 90 Tagen vorschreiben?" },
      { id: "sso_active", label: "Single-Sign-On (SSO) für Active Directory?" },
      { id: "ip_lockout", label: "IP-Adressbereich-Einschränkung für Disponenten?" },
      { id: "dispo_read_only", label: "Beobachter-Rolle darf Dispo-Board nur ansehen?" },
      { id: "operator_sensitive_hide", label: "Sicherheitsrelevante Kundendaten für Standard-Operatoren verbergen?" }
    ]
  },
  {
    title: "5. System-Einstellungen",
    questions: [
      { id: "dark_mode_default", label: "Standardmäßig Dark-Mode-Ansicht für alle Benutzer?" },
      { id: "mail_notification_instant", label: "Sofortige E-Mail-Benachrichtigung bei Tourabbruch?" },
      { id: "daily_server_backup", label: "Tägliches automatisches Cloud-Daten-Backup aktivieren?" },
      { id: "auto_archive_365", label: "Automatische Tourarchivierung nach 365 Tagen?" },
      { id: "maintenance_notification", label: "Benachrichtigung über geplante Wartungsfenster anzeigen?" },
      { id: "api_logging_active", label: "Ausführliches API-Logging zur Fehleranalyse?" }
    ]
  }
];
